import React, { useContext, useEffect } from 'react'
import { useCurrentUser } from '@hooks/useCurrentUser'
import Text from '@components/Text'
import Heading from '@components/Heading'
import Button from '@components/Button'
import { BUTTON_SIZE, BUTTON_TYPE, DIVIDER_SIZE, HEADING_TYPE, TEXT_SIZES } from '@helpers/constants'
import { AppContext } from '@state/context'
import { setShowOnboardingBlock } from '@state/actions/appActions'
import { AnimatePresence, motion } from 'framer-motion'
import { useUserSchoolClasses } from '@hooks/useUserSchoolClasses'
import Divider from '@components/Divider'
import { useOwnBooksAllowed } from '@hooks/useOwnBooksAllowed'

const LevelDot = ({ level, filled }) => {
  return (
    <div
      className={`flex items-center justify-center text-xl font-bold border-2 h-12 w-12 rounded-full ${
        filled
          ? 'border-primaryGreen bg-primaryGreen text-white '
          : 'border-mediumGrey text-mediumGrey'
      }`}
    >
      {level}
    </div>
  )
}

const Line = ({ filled }) => {
  return (
    <div
      className={`absolute ${filled ? 'bg-primaryGreen' : 'bg-mediumGrey'}`}
      style={{
        top: 'calc(50% - 1px)',
        width: 'calc(100% - 100px)',
        height: '2px',
        left: 'calc(50% + 50px)',
      }}
    />
  )
}

// If the user completes the onboarding steps, hide the block next login but show it until then.
let hideBlock = undefined

const OnboardingProgressBlock = () => {
  const { currentUser } = useCurrentUser()
  const { schoolClasses } = useUserSchoolClasses()
  const [{ app: { showOnboardingProgressBlock } }, dispatch] = useContext(AppContext)
  const libraryPreamble = useOwnBooksAllowed()
    ? 'Se alla böcker och avsnitt. Prova att spara och dela.'
    : 'Se alla böcker. Prova att spara och dela.'

  useEffect(() => {
    if (hideBlock === undefined && currentUser) {
      hideBlock =
        currentUser.hasVisitedLibrary &&
        (currentUser.hasValidPubAgreement ? currentUser.hasVisitedHelp : currentUser.hasCreatedStudents) &&
        currentUser.hasCreatedSchoolGroups
    }
  }, [currentUser])

  if (!currentUser || hideBlock) return null

  const items = currentUser?.hasValidPubAgreement
    ? [
        {
          heading: 'Upptäck biblioteket',
          preamble: libraryPreamble,
          link: 'bibliotek',
          linkTitle: 'Bibliotek',
          filled: currentUser.hasVisitedLibrary,
        },
        {
          heading: 'Skapa grupper',
          preamble: 'Grupperna hjälper dig att utmana eleverna på rätt nivå.',
          link: 'lararrum',
          linkTitle: 'Mitt lärarrum',
          filled: currentUser.hasVisitedLibrary && currentUser.hasCreatedSchoolGroups,
        },
        {
          heading: 'Få hjälp',
          preamble: 'Du kan alltid få svar på dina frågor.',
          link: 'hjalp',
          linkTitle: 'Guide',
          filled: currentUser.hasVisitedLibrary && currentUser.hasCreatedSchoolGroups && currentUser.hasVisitedHelp,
        },
      ]
    : [
        {
          heading: 'Upptäck biblioteket',
          preamble: libraryPreamble,
          link: 'bibliotek',
          linkTitle: 'Bibliotek',
          filled: currentUser.hasVisitedLibrary,
        },
        {
          heading: 'Låt dina elever skriva in sig',
          preamble:
            'När dina elever är inskrivna kan du dela böcker med dem och skapa nivågrupper.',
          link:
            'klass/' + schoolClasses?.find((sc) => sc.isPrivate)?.id ??
            schoolClasses?.[0]?.id,
          linkTitle: 'Min klass',
          filled: currentUser.hasVisitedLibrary && currentUser.hasCreatedStudents,
        },
        {
          heading: 'Skapa grupper',
          preamble: 'Grupperna hjälper dig att utmana eleverna på rätt nivå.',
          link: 'lararrum',
          linkTitle: 'Mitt lärarrum',
          filled: currentUser.hasVisitedLibrary && currentUser.hasCreatedStudents && currentUser.hasCreatedSchoolGroups,
        },
      ]

  return (
    <div className='relative overflow-hidden'>
      <AnimatePresence initial={false}>
        {showOnboardingProgressBlock &&
          <motion.div
            className={
              'flex flex-col justify-center items-center bg-white mx-auto text-center max-w-[1100px]'
            }
            initial={{height: '0'}}
            animate={{height: 'auto'}}
            exit={{height: '0'}}
          >
            <Divider size={DIVIDER_SIZE.SMALL} />
            <div className={'absolute w-[100vw] mr-16 top-4 text-right'}>
              <Button
                handleClick={() => {
                  dispatch(setShowOnboardingBlock(false))
                }}
                size={BUTTON_SIZE.NO_PADDING}
                icon={'CloseMedium'}
                iconRight
              >
                Stäng
              </Button>
            </div>

            <Heading extraClasses={'mt-6 mb-4'} headingLevel={HEADING_TYPE.H1} size={TEXT_SIZES.XXLARGE}>
              Kom igång med Doklet!
            </Heading>

            <div className={'flex w-full items-center justify-center mt-4'}>
              {items.map((item, index) => (
                <div
                  key={index}
                  className={'flex justify-center relative basis-1/3'}
                >
                  <LevelDot level={index + 1} filled={item.filled} />
                  {index < items.length - 1 &&
                    <Line filled={item.filled} />
                  }
                </div>
              ))}
            </div>

            <div className='mb-8 flex justify-center w-full items-stretch'>
              {items.map((item, index) => (
                <div key={index} className={'p-4 basis-1/3 flex flex-col justify-between items-center'}>
                  <div>
                    <Heading headingLevel={HEADING_TYPE.H4} size={TEXT_SIZES.LARGE}>
                      {item.heading}
                    </Heading>
                    <div className={'mb-4 max-w-[300px]'}>
                      <Text>{item.preamble}</Text>
                    </div>
                  </div>

                  <Button
                    type={BUTTON_TYPE.SECONDARY}
                    to={item.link}
                    iconClasses={'w-4 h-4'}
                    icon={'ArrowRight'}
                    iconRight
                  >
                    {item.linkTitle}
                  </Button>
                </div>
              ))}
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </div>
  )
}

OnboardingProgressBlock.propTypes = {}

export default OnboardingProgressBlock
